import { useCallback, useEffect } from 'react';
import useSocket from '../../../../shared/hooks/use-socket';
import { useReducerAtom } from 'jotai/utils';
import { socketConnectionAtom, socketConnectionReducer } from '../../../../shared/states/socket-connection';
import { SocketConnectionReducerActionTypes, SocketName } from '../../../../shared/typings/socket-connection';
import { getOrganizationsSocket } from '../../../../infra/sockets/organization-socket';
import { WACallPayload, WACallsEventTypes } from '../../../call-manager/typings';
import { WAGroupEventTypes, WAGroupPayload, WAGroupStatus } from '../../../wa-groups/typings/groups';
import { useSetAtom } from 'jotai';
import { chatRowSetter } from '../states/chat';
import { CHAT_UPDATE_TYPE } from '../typings/chat';
import { broadcastGroupCFCountUpdateAtom } from '../../../../shared/states/template';

export const useOrganizationSocket = () => {
  const [_, dispatch] = useReducerAtom(socketConnectionAtom, socketConnectionReducer);

  const { socket: organisationSocket } = useSocket(getOrganizationsSocket, undefined, false, undefined);

  const setChatRow = useSetAtom(chatRowSetter);
  const setBroadcastGroupCFCountUpdate = useSetAtom(broadcastGroupCFCountUpdateAtom);

  const onCallConnectRequest = useCallback((payload: WACallPayload) => {
    switch (payload.event) {
      case WACallsEventTypes.INCOMMING_CALL:
        const {
          customerName,
          customerNumber,
          wabaNumber,
          wabaName,
          callId,
          sdp,
          sdpType,
          startTimestamp,
          expiryTimestamp,
        } = payload;
        // check if expiry_time is in the past
        if (payload.expiryTimestamp && new Date(payload.expiryTimestamp) < new Date()) {
          return;
        }
        window.postMessage(
          {
            type: 'INCOMING_CALL',
            payload: {
              customerName,
              customerNumber,
              wabaNumber,
              wabaName,
              callId,
              sdp,
              sdpType,
              dateCreated: new Date(Number(startTimestamp)).toISOString(),
              expiryTimestamp: expiryTimestamp ? Number(expiryTimestamp) : undefined,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_TERMINATE:
        const { duration, direction, startTime, endTime, callId: terminatedCallId, endedBy } = payload;
        window.postMessage(
          {
            type: 'CALL_TERMINATE',
            payload: {
              duration,
              direction,
              startTime,
              endTime,
              endedBy,
              callId: terminatedCallId,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_PICKED:
        const { userId, callId: pickedCallId } = payload;
        window.postMessage(
          {
            type: 'CALL_PICKED',
            payload: {
              userId,
              callId: pickedCallId,
              direction: payload.direction,
              sdp: payload.sdp,
              sdpType: payload.sdpType,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_STATUS_UPDATES:
        const { callId: statusCallId, status } = payload;
        window.postMessage(
          {
            type: 'CALL_STATUS_UPDATES',
            payload: { callId: statusCallId, status },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_PERMISSION:
        window.postMessage(
          {
            type: 'CALL_PERMISSION',
            payload,
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_RECONNECTED:
        window.postMessage(
          {
            type: 'CALL_RECONNECTED',
            payload,
          },
          '*'
        );
        break;
      default:
        break;
    }
  }, []);

  const onWAGroupSocketEventHandler = useCallback((payload: WAGroupPayload) => {
    switch (payload.event) {
      case WAGroupEventTypes.GROUP_INVITE_LINK_READY:
        const { groupChatId, inviteLink } = payload;

        setChatRow({
          type: CHAT_UPDATE_TYPE.UPDATE,
          partial: true,
          reOrder: false,
          data: {
            id: groupChatId,
            inviteLink,
            waGroupStatus: WAGroupStatus.SUCCESS,
          },
        });
        break;
      case WAGroupEventTypes.GROUP_PARTICIPANT_COUNT_UPDATED:
        const { participantCount } = payload;

        setChatRow({
          type: CHAT_UPDATE_TYPE.UPDATE,
          partial: true,
          reOrder: false,
          data: {
            id: payload.groupChatId,
            memberCount: participantCount,
          },
        });
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    /**
     * This event handler is for incoming call requests
     */
    organisationSocket.on('wa-calls', onCallConnectRequest);

    /**
     * This event handler is for WhatsApp Group events
     * */
    organisationSocket.on('wa-group', onWAGroupSocketEventHandler);

    organisationSocket.on('broadcast-group-cf-count-updates', setBroadcastGroupCFCountUpdate);

    return () => {
      organisationSocket.off('wa-calls', onCallConnectRequest);
      organisationSocket.off('wa-group', onWAGroupSocketEventHandler);
      organisationSocket.off('broadcast-group-cf-count-updates', setBroadcastGroupCFCountUpdate);
    };
  }, [onCallConnectRequest]);
};
